<template>
  <div class="status-timeline-container">
    <div class="status-header">
      <h3 class="timeline-title">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="status-loading">
      <span>{{ $t('common.loading') }}...</span>
    </div>

    <!-- Status Timeline -->
    <div v-else class="status-timeline">
      <div
        v-for="(step, index) in timelineSteps"
        :key="`step-${index}`"
        class="status-step"
        :class="{
          current: step.isCurrent,
          completed: step.isCompleted
        }"
      >
        <div class="status-icon">
          <component :is="getStatusIcon(step.status, step)" />
        </div>
        <div class="status-info">
          <div class="status-label">{{ getStatusLabel(step.status) }}</div>
          <div class="status-date" v-if="step.date">
            {{ step.date }}
          </div>
        </div>
      </div>
    </div>

    <!-- Status Action Buttons -->
    <PermissionGuard v-if="updatePermission" :permission="updatePermission" :project-id="projectId">
      <div v-if="possibleStatuses.length > 0" class="status-actions">
        <button
          v-for="status in possibleStatuses"
          :key="status"
          @click="handleStatusChange(status)"
          class="status-action-btn"
          :class="getStatusActionClass(status)"
          :title="$t(transitionToKey, { status: getStatusLabel(status) })"
        >
          <component :is="getActionIcon(status)" />
          <span>{{ getActionLabel(status) }}</span>
        </button>
      </div>
    </PermissionGuard>

    <!-- Status Transition Modal Slot -->
    <slot 
      name="modal" 
      :show="showModal" 
      :target-status="targetStatus"
      :on-success="handleModalSuccess"
      :on-close="closeModal"
    />
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, h, type VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import PermissionGuard from '@/components/common/PermissionGuard.vue'

// Types
interface TimelineStep {
  status: string
  date: string | null
  isCurrent: boolean
  isCompleted: boolean
}

interface StatusHistoryDto {
  id: number
  fromStatus: string | null
  toStatus: string
  changedByLogin: string
  changedByName: string
  changedAt: string
  formattedChangedAt: string
  event: string
  comment?: string
}

interface StatusIconConfig {
  [key: string]: (step: TimelineStep) => VNode
}

// Props
const props = defineProps<{
  /** Title of the timeline */
  title: string
  /** Current status of the entity */
  currentStatus: string
  /** Timeline history data */
  timeline: StatusHistoryDto[]
  /** Possible next statuses */
  possibleStatuses: string[]
  /** Loading state */
  loading?: boolean
  /** i18n key prefix for status labels (e.g., 'phaseWorkspace.statusOptions' or 'projects.statusOptions') */
  i18nPrefix: string
  /** i18n key prefix for action button labels (e.g., 'phaseWorkspace.statusActions') - uses verbs for buttons */
  actionI18nPrefix?: string
  /** i18n key for transition tooltip (e.g., 'phaseWorkspace.statusTransition.transitionTo') */
  transitionToKey: string
  /** Permission name for update action (optional) */
  updatePermission?: string
  /** Project ID for permission checking (optional) */
  projectId?: number
  /** Status icon configurations */
  statusIcons?: StatusIconConfig
}>()

// Emits
const emit = defineEmits<{
  statusChange: [status: string]
  refresh: []
}>()

// State
const showModal = ref(false)
const targetStatus = ref<string | null>(null)

// Composables
const { t } = useI18n()

// Computed timeline steps
const timelineSteps = computed<TimelineStep[]>(() => {
  if (!props.timeline || props.timeline.length === 0) {
    // If no timeline, show current status only
    return [{
      status: props.currentStatus,
      date: null,
      isCurrent: true,
      isCompleted: false
    }]
  }
  
  const steps: TimelineStep[] = []
  
  // Add the initial status (fromStatus of the first transition)
  if (props.timeline[0].fromStatus) {
    steps.push({
      status: props.timeline[0].fromStatus,
      date: null, // Initial status doesn't have a date
      isCurrent: false,
      isCompleted: true
    })
  }
  
  // Add all transitions
  props.timeline.forEach((entry: StatusHistoryDto, index: number) => {
    const isLast = index === props.timeline.length - 1
    steps.push({
      status: entry.toStatus,
      date: entry.formattedChangedAt || null,
      isCurrent: isLast && entry.toStatus === props.currentStatus,
      isCompleted: !isLast || entry.toStatus !== props.currentStatus
    })
  })
  
  return steps
})

// Get status label from i18n (for timeline display)
const getStatusLabel = (status: string): string => {
  return t(`${props.i18nPrefix}.${status}`)
}

// Get action button label from i18n (uses verbs)
const getActionLabel = (status: string): string => {
  // Use actionI18nPrefix if provided, otherwise fallback to i18nPrefix
  const prefix = props.actionI18nPrefix || props.i18nPrefix
  
  // Context-aware action labels for specific transitions
  // For issues: OPEN -> IN_PROGRESS should be "START", PAUSED -> IN_PROGRESS should be "RESUME"
  // For phases: NOT_STARTED -> IN_PROGRESS should be "START", ON_HOLD -> IN_PROGRESS should be "RESUME"
  // For tasks: PENDING -> IN_PROGRESS should be "START", ON_HOLD/PAUSED -> IN_PROGRESS should be "RESUME"
  if (status === 'IN_PROGRESS' && props.currentStatus) {
    // Starting work: OPEN/NOT_STARTED/PENDING -> IN_PROGRESS
    if (['OPEN', 'NOT_STARTED', 'PENDING'].includes(props.currentStatus)) {
      // Try to get START translation, fallback to IN_PROGRESS if not found
      const startKey = `${prefix}.START`
      const translation = t(startKey)
      if (translation !== startKey) {
        return translation
      }
    } 
    // Resuming work: PAUSED/ON_HOLD -> IN_PROGRESS
    else if (['PAUSED', 'ON_HOLD'].includes(props.currentStatus)) {
      // Try to get RESUME translation, fallback to IN_PROGRESS if not found
      const resumeKey = `${prefix}.RESUME`
      const translation = t(resumeKey)
      if (translation !== resumeKey) {
        return translation
      }
    }
  }
  
  return t(`${prefix}.${status}`)
}

// Get CSS class for status action button
const getStatusActionClass = (status: string): string => {
  return `status-action-btn--${status.toLowerCase()}`
}

// Handle status change button click
const handleStatusChange = (status: string) => {
  targetStatus.value = status
  showModal.value = true
  emit('statusChange', status)
}

// Handle modal success
const handleModalSuccess = () => {
  showModal.value = false
  emit('refresh')
}

// Close modal
const closeModal = () => {
  showModal.value = false
  targetStatus.value = null
}

// Default status icons
const defaultStatusIcons: StatusIconConfig = {
  OPEN: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('circle', {
      cx: 12,
      cy: 12,
      r: 8,
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2
    })
  ]),
  
  NOT_STARTED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('circle', {
      cx: 12,
      cy: 12,
      r: 8,
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2
    })
  ]),
  
  DRAFT: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('path', {
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2,
      d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8'
    })
  ]),
  
  PLANNED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('path', {
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2,
      d: 'M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1'
    })
  ]),
  
  IN_PROGRESS: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polygon', {
      fill: getIconFill(step),
      points: '5 3 19 12 5 21 5 3'
    })
  ]),
  
  ACTIVE: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polygon', {
      fill: getIconFill(step),
      points: '5 3 19 12 5 21 5 3'
    })
  ]),
  
  ON_HOLD: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 6,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      }),
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 14,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      })
    ])
  ]),
  
  PAUSED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 6,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      }),
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 14,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      })
    ])
  ]),
  
  RESOLVED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polyline', {
      stroke: getIconStroke(step),
      'stroke-width': 2,
      fill: 'none',
      points: '20 6 9 17 4 12'
    })
  ]),
  
  COMPLETED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polyline', {
      stroke: getIconStroke(step),
      'stroke-width': 2,
      fill: 'none',
      points: '20 6 9 17 4 12'
    })
  ]),
  
  CLOSED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('circle', { 
      cx: 12, 
      cy: 12, 
      r: 10, 
      fill: 'none', 
      stroke: getIconStroke(step), 
      'stroke-width': 2 
    }),
    h('path', { 
      d: 'M12,6V12L16,14', 
      fill: 'none', 
      stroke: getIconStroke(step), 
      'stroke-width': 2 
    })
  ]),
  
  REJECTED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 18,
        y1: 6,
        x2: 6,
        y2: 18
      }),
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 6,
        y1: 6,
        x2: 18,
        y2: 18
      })
    ])
  ]),
  
  CANCELLED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 18,
        y1: 6,
        x2: 6,
        y2: 18
      }),
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 6,
        y1: 6,
        x2: 18,
        y2: 18
      })
    ])
  ]),
  
  ARCHIVED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('path', {
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2,
      d: 'M3,3H21V7H3V3M4,8H20V21H4V8M9.5,11A0.5,0.5 0 0,0 9,11.5V13H15V11.5A0.5,0.5 0 0,0 14.5,11H9.5Z'
    })
  ])
}

// Get status icon
const getStatusIcon = (status: string, step: TimelineStep): VNode => {
  const icons = props.statusIcons || defaultStatusIcons
  const iconFn = icons[status] || defaultStatusIcons.NOT_STARTED
  return iconFn(step)
}

// Default action icons (filled versions for buttons)
const defaultActionIcons: StatusIconConfig = {
  OPEN: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('circle', { fill: 'currentColor', cx: 12, cy: 12, r: 8 })
  ]),
  
  NOT_STARTED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('circle', { fill: 'currentColor', cx: 12, cy: 12, r: 8 })
  ]),
  
  DRAFT: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6z'
    })
  ]),
  
  PLANNED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1'
    })
  ]),
  
  IN_PROGRESS: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M8,5.14V19.14L19,12.14L8,5.14Z' })
  ]),
  
  ACTIVE: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M8,5.14V19.14L19,12.14L8,5.14Z' })
  ]),
  
  ON_HOLD: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M14,19H18V5H14M6,19H10V5H6V19Z' })
  ]),
  
  PAUSED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M14,19H18V5H14M6,19H10V5H6V19Z' })
  ]),
  
  RESOLVED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z'
    })
  ]),
  
  COMPLETED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z'
    })
  ]),
  
  CLOSED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M12,2C6.47,2 2,6.47 2,12C2,17.53 6.47,22 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2M17,13H11V7H12.5V11.5H17V13Z'
    })
  ]),
  
  REJECTED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z'
    })
  ]),
  
  CANCELLED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z'
    })
  ]),
  
  ARCHIVED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M3,3H21V7H3V3M4,8H20V21H4V8M9.5,11A0.5,0.5 0 0,0 9,11.5V13H15V11.5A0.5,0.5 0 0,0 14.5,11H9.5Z'
    })
  ])
}

// Get action icon
const getActionIcon = (status: string): VNode => {
  const iconFn = defaultActionIcons[status] || defaultActionIcons.NOT_STARTED
  return iconFn({ status, date: null, isCurrent: false, isCompleted: false })
}

// Get icon fill color based on step
const getIconFill = (step: TimelineStep): string => {
  if (step.isCurrent || step.isCompleted) {
    return 'white'
  }
  return 'none'
}

// Get icon stroke color based on step
const getIconStroke = (step: TimelineStep): string => {
  if (step.isCurrent || step.isCompleted) {
    return 'white'
  }
  return 'currentColor'
}
</script>

<style scoped>
/* Timeline Container */
.status-timeline-container {
  background: var(--color-surface, #ffffff);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid var(--border-color, #e5e7eb);
  /* Container should stay within bounds */
  max-width: 100%;
  overflow: hidden;
}

.status-header {
  margin-bottom: 2rem;
}

.timeline-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary, #111827);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.timeline-title::before {
  content: '⏱';
  font-size: 1.5rem;
}

/* Loading State */
.status-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--text-secondary, #6b7280);
  font-size: 0.875rem;
}

/* Status Timeline */
.status-timeline {
  display: flex;
  align-items: flex-start;
  gap: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2rem 0;
  margin-bottom: 2rem;
  background: linear-gradient(to bottom, transparent 0%, #f9fafb 50%, transparent 100%);
  border-radius: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.status-timeline::-webkit-scrollbar {
  height: 8px;
}

.status-timeline::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.status-timeline::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.status-timeline::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.status-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  min-width: 140px;
  width: 140px;
  flex-shrink: 0;
  position: relative;
}

.status-step::after {
  content: '';
  position: absolute;
  top: 24px;
  left: 60%;
  width: 100%;
  height: 3px;
  background: var(--border-color, #e5e7eb);
  z-index: 0;
}

.status-step:last-child::after {
  display: none;
}

.status-step.current::after {
  background: linear-gradient(to right, var(--primary, #3b82f6), var(--border-color, #e5e7eb));
}

.status-step.completed::after {
  background: var(--success, #10b981);
}

/* Status Icon */
.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid var(--border-color, #e5e7eb);
  background: var(--surface, #ffffff);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  color: var(--text-muted, #9ca3af);
}

.status-step.current .status-icon {
  border-color: var(--primary, #3b82f6);
  background: var(--primary, #3b82f6);
  color: white;
  box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.1),
              0 4px 12px rgba(59, 130, 246, 0.3);
  transform: scale(1.1);
  animation: statusPulse 2s ease-in-out infinite;
}

@keyframes statusPulse {
  0%, 100% { 
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.1),
                0 4px 12px rgba(59, 130, 246, 0.3);
    transform: scale(1.1);
  }
  50% { 
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0.15),
                0 6px 16px rgba(59, 130, 246, 0.4);
    transform: scale(1.15);
  }
}

.status-step.completed .status-icon {
  border-color: var(--success, #10b981);
  background: var(--success, #10b981);
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

/* Status Info */
.status-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
}

.status-label {
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: center;
  color: var(--text-secondary, #6b7280);
  white-space: nowrap;
}

.status-step.current .status-label {
  color: var(--primary, #3b82f6);
  font-weight: 700;
  font-size: 0.875rem;
}

.status-step.completed .status-label {
  color: var(--success, #10b981);
  font-weight: 600;
}

.status-date {
  font-size: 0.6875rem;
  color: var(--text-muted, #9ca3af);
  text-align: center;
  white-space: nowrap;
  font-weight: 400;
  font-family: 'Courier New', monospace;
}

.status-step.current .status-date {
  color: var(--primary, #3b82f6);
  font-weight: 600;
}

.status-step.completed .status-date {
  color: var(--text-secondary, #6b7280);
}

.status-step.current {
  color: var(--primary, #3b82f6);
}

.status-step.completed  {
  color: var(--text-secondary, #6b7280);
}

/* Status Actions */
.status-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-top: 1rem;
  border-top: 2px solid var(--border-color, #e5e7eb);
}

.status-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1.25rem;
  border: 2px solid;
  border-radius: 8px;
  background: var(--surface, #ffffff);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.status-action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: currentColor;
  opacity: 0;
  transition: opacity 0.2s;
}

.status-action-btn:hover::before {
  opacity: 0.08;
}

.status-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.status-action-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.status-action-btn svg {
  width: 18px;
  height: 18px;
  position: relative;
  z-index: 1;
}

.status-action-btn span {
  position: relative;
  z-index: 1;
}

/* Status-specific action button styles */
.status-action-btn--open {
  border-color: #3b82f6;
  color: #3b82f6;
}

.status-action-btn--open:hover {
  background: #eff6ff;
  border-color: #2563eb;
  color: #1d4ed8;
}

.status-action-btn--not_started {
  border-color: #9ca3af;
  color: #6b7280;
}

.status-action-btn--not_started:hover {
  background: #f9fafb;
  border-color: #6b7280;
  color: #4b5563;
}

.status-action-btn--draft {
  border-color: #9ca3af;
  color: #6b7280;
}

.status-action-btn--draft:hover {
  background: #f9fafb;
  border-color: #6b7280;
  color: #4b5563;
}

.status-action-btn--planned {
  border-color: #3b82f6;
  color: #3b82f6;
}

.status-action-btn--planned:hover {
  background: #eff6ff;
  border-color: #2563eb;
  color: #1d4ed8;
}

.status-action-btn--in_progress,
.status-action-btn--active {
  border-color: #3b82f6;
  color: #3b82f6;
}

.status-action-btn--in_progress:hover,
.status-action-btn--active:hover {
  background: #eff6ff;
  border-color: #2563eb;
  color: #1d4ed8;
}

.status-action-btn--on_hold {
  border-color: #f59e0b;
  color: #f59e0b;
}

.status-action-btn--on_hold:hover {
  background: #fffbeb;
  border-color: #d97706;
  color: #b45309;
}

.status-action-btn--paused {
  border-color: #f59e0b;
  color: #f59e0b;
}

.status-action-btn--paused:hover {
  background: #fffbeb;
  border-color: #d97706;
  color: #b45309;
}

.status-action-btn--resolved {
  border-color: #10b981;
  color: #10b981;
}

.status-action-btn--resolved:hover {
  background: #ecfdf5;
  border-color: #059669;
  color: #047857;
}

.status-action-btn--completed {
  border-color: #10b981;
  color: #10b981;
}

.status-action-btn--completed:hover {
  background: #ecfdf5;
  border-color: #059669;
  color: #047857;
}

.status-action-btn--closed {
  border-color: #6b7280;
  color: #6b7280;
}

.status-action-btn--closed:hover {
  background: #f9fafb;
  border-color: #4b5563;
  color: #374151;
}

.status-action-btn--rejected {
  border-color: #ef4444;
  color: #ef4444;
}

.status-action-btn--rejected:hover {
  background: #fef2f2;
  border-color: #dc2626;
  color: #b91c1c;
}

.status-action-btn--cancelled {
  border-color: #ef4444;
  color: #ef4444;
}

.status-action-btn--cancelled:hover {
  background: #fef2f2;
  border-color: #dc2626;
  color: #b91c1c;
}

.status-action-btn--archived {
  border-color: #6b7280;
  color: #6b7280;
}

.status-action-btn--archived:hover {
  background: #f9fafb;
  border-color: #4b5563;
  color: #374151;
}

/* Responsive */
@media (max-width: 768px) {
  .status-timeline-container {
    padding: 1.5rem;
  }
  
  .status-timeline {
    padding: 1.5rem 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
  
  .status-step {
    min-width: 120px;
    width: 120px;
  }
  
  .status-icon {
    width: 42px;
    height: 42px;
  }
  
  .status-actions {
    gap: 0.5rem;
  }
  
  .status-action-btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
}
</style>
